import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../CSS/Calendar.css';
import useIsMobile from '../Components/UseIsMobile.js';

const localizer = momentLocalizer(moment);

const YourCalendarComponent = () => {
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSector, setSelectedSector] = useState('');
  const [groupResponsible, setGroupResponsible] = useState('');
  const [targetedEntity, setTargetedEntity] = useState('');
  const [selectedDropdownFilter, setSelectedDropdownFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedIndustryFilter, setSelectedIndustryFilter] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const isMobile = useIsMobile();

  const handleSearch = (event) => setSearchTerm(event.target.value);

  // simple select behavior (no toggle)
  const handleSectorChange = (event) => {
    setSelectedSector(event.target.value);
  };

  const handleGroupResponsibleChange = (event) => {
    setGroupResponsible(event.target.value);
  };

  const handleTargetedEntityChange = (event) => {
    setTargetedEntity(event.target.value);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const handleClear = () => {
    setSearchTerm('');
    setSelectedSector('');
    setGroupResponsible('');
    setTargetedEntity('');
    setSelectedDropdownFilter('');
    setStartDate('');
    setEndDate('');
    setSelectedIndustryFilter('');
    setSelectedRegion('');
    setCityFilter('');
    setStateFilter('');
    setCountryFilter('');
  };

  const applyDropdownFilter = (event) => {
    setSelectedDropdownFilter(event.target.value);
  };

  const applyIndustryFilter = (event) => {
    setSelectedIndustryFilter(event.target.value);
  };

  // simple select behavior (no toggle)
  const handleRegionChange = (event) => {
    setSelectedRegion(event.target.value);
  };

  useEffect(() => {
    fetch('/api/hello')
      .then((response) => response.json())
      .then((data) => {
        const filteredReports = data.reports.filter(
          (report) => report.fields.field_9 === 'Protest Planned'
        );
        setReports(filteredReports);
        setOrgs(data.orgs);
      })
      .catch((error) => {
        console.error('Error fetching reports data:', error);
      });
  }, []);

  useEffect(() => {
    // orgs side-effects if needed
  }, [orgs]);

  const filterReports = (
    reports,
    searchTerm,
    selectedSector,
    groupResponsible,
    targetedEntity,
    selectedDropdownFilter,
    startDate,
    endDate,
    selectedIndustryFilter,
    selectedRegion,
    cityFilter,
    stateFilter,
    countryFilter,
    orgs
  ) => {
    const groupResponsibleLower = groupResponsible?.toLowerCase();
    let relatedGroupNames = new Set();

    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    const cityLower = cityFilter?.trim().toLowerCase();
    const stateLower = stateFilter?.trim().toLowerCase();
    const countryLower = countryFilter?.trim().toLowerCase();

    if (groupResponsibleLower && orgs.length > 0) {
      orgs.forEach((org) => {
        const title = org.fields?.Title?.toLowerCase();
        const aka = org.fields?.AKA?.toLowerCase();
        const oldName = org.fields?.OldName?.toLowerCase();

        if (
          title?.includes(groupResponsibleLower) ||
          aka?.includes(groupResponsibleLower) ||
          oldName?.includes(groupResponsibleLower)
        ) {
          if (title) relatedGroupNames.add(title);
          if (aka) relatedGroupNames.add(aka);
          if (oldName) relatedGroupNames.add(oldName);
        }
      });

      if (relatedGroupNames.size === 0) {
        relatedGroupNames.add(groupResponsibleLower);
      }
    }

    return (reports || []).filter((report) => {
      if (!report.fields) return false;

      const fields = report.fields;
      const sector = fields.field_6 || [];
      const searchTermLower = searchTerm?.toLowerCase();

      const titleMatches =
        !searchTerm || fields.Title?.toLowerCase().includes(searchTermLower);

      const groupResponsibleMatches =
        !groupResponsible ||
        [...relatedGroupNames].some((name) => {
          const nameLower = name.toLowerCase();
          return (
            fields.field_10?.toLowerCase().includes(nameLower) ||
            (Array.isArray(fields.GroupResponsibleLU) &&
              fields.GroupResponsibleLU.some((item) =>
                item.LookupValue?.toLowerCase().includes(nameLower)
              ))
          );
        });

      const targetedEntityMatches =
        !targetedEntity ||
        (fields.field_11 &&
          fields.field_11.toLowerCase().includes(targetedEntity.toLowerCase()));

      const dropdownFilterMatches =
        !selectedDropdownFilter ||
        (fields.field_9 &&
          fields.field_9
            .toLowerCase()
            .includes(selectedDropdownFilter.toLowerCase()));

      const regionMatches =
        !selectedRegion || (fields.Region0 && fields.Region0 === selectedRegion);

      const cityMatches =
        !cityLower ||
        (fields.field_5 && fields.field_5.toLowerCase().includes(cityLower));

      const stateMatches =
        !stateLower ||
        (fields.field_4 && fields.field_4.toLowerCase().includes(stateLower));

      const countryMatches =
        !countryLower ||
        (fields.field_3 &&
          fields.field_3.toLowerCase().includes(countryLower));

      const reportStartRaw = fields.field_2_formatted;
      const reportStartDate = reportStartRaw ? new Date(reportStartRaw) : null;

      if ((start || end) && !reportStartDate) {
        return false;
      }

      const startDateMatches =
        !start || (reportStartDate && reportStartDate >= start);
      const endDateMatches =
        !end || (reportStartDate && reportStartDate <= end);

      const industryMatches =
        !selectedIndustryFilter ||
        (fields.ARIndustry &&
          fields.ARIndustry
            .toLowerCase()
            .includes(selectedIndustryFilter.toLowerCase())) ||
        (fields.ESIndustry &&
          fields.ESIndustry
            .toLowerCase()
            .includes(selectedIndustryFilter.toLowerCase())) ||
        (fields.PharmaIndustry &&
          fields.PharmaIndustry
            .toLowerCase()
            .includes(selectedIndustryFilter.toLowerCase()));

      if (
        !selectedSector &&
        !searchTerm &&
        !groupResponsible &&
        !targetedEntity &&
        !selectedDropdownFilter &&
        !selectedRegion &&
        !cityLower &&
        !stateLower &&
        !countryLower
      ) {
        return startDateMatches && endDateMatches && industryMatches;
      }

      if (selectedSector) {
        const sectorMatches = sector.some((sec) =>
          sec.toLowerCase().includes(selectedSector.toLowerCase())
        );

        return (
          titleMatches &&
          sectorMatches &&
          groupResponsibleMatches &&
          targetedEntityMatches &&
          dropdownFilterMatches &&
          regionMatches &&
          cityMatches &&
          stateMatches &&
          countryMatches &&
          startDateMatches &&
          endDateMatches &&
          industryMatches
        );
      }

      return (
        titleMatches &&
        groupResponsibleMatches &&
        targetedEntityMatches &&
        dropdownFilterMatches &&
        regionMatches &&
        cityMatches &&
        stateMatches &&
        countryMatches &&
        startDateMatches &&
        endDateMatches &&
        industryMatches
      );
    });
  };

  const filteredReports = filterReports(
    reports,
    searchTerm,
    selectedSector,
    groupResponsible,
    targetedEntity,
    selectedDropdownFilter,
    startDate,
    endDate,
    selectedIndustryFilter,
    selectedRegion,
    cityFilter,
    stateFilter,
    countryFilter,
    orgs
  );

  const events = filteredReports.map((report) => ({
    title: report.fields.Title,
    start: moment(report.fields.field_2_formatted).toDate(),
    end: report.fields.EndDate_formatted
      ? moment(report.fields.EndDate_formatted).endOf('day').toDate()
      : moment(report.fields.field_2_formatted).toDate(),
    url: report.fields.Links1,
  }));

  const EventComponent = ({ event }) => {
    const handleOpen = (e) => {
      e.preventDefault();

      const userEmail = localStorage.getItem('user-email');

      const lastPart = (event.url || '').split('/').pop() || '';
      const fileName = decodeURIComponent(lastPart.split('?')[0]);
      const proxyUrl = `/api/reports/${encodeURIComponent(fileName)}`;

      fetch('/api/log-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          reportName: fileName,
          reportType: 'Calendar',
          userEmail,
        }),
      }).catch(() => {});

      window.open(proxyUrl, '_blank', 'noopener,noreferrer');
    };

    return (
      <div className="report-item">
        <a
          href={event.url}
          target="_blank"
          rel="noopener noreferrer"
          className="report-title"
          onClick={handleOpen}
        >
          {event.title}
        </a>
      </div>
    );
  };

  return (
    <div className="calendar-page">
      {/* DESKTOP: sidebar + title above (calendar+grid) */}
      {!isMobile && (
        <div className="calendar-layout">
          {/* LEFT COLUMN: SIDEBAR */}
          <aside className="calendar-filters-sidebar">
            <h2 className="filters-title">Filters</h2>

            {/* Sector dropdown */}
            <div className="filter-group">
              <label htmlFor="sectorFilter">Sector</label>
              <select
                id="sectorFilter"
                value={selectedSector}
                onChange={handleSectorChange}
              >
                <option value="">All Sectors</option>
                <option value="Animal Rights">Animal Rights</option>
                <option value="Energy Sector">Energy Sector</option>
                <option value="Pharmaceuticals">Pharmaceuticals</option>
                <option value="Critical Insights">Critical Insights</option>
              </select>
            </div>

            {/* Region dropdown */}
            <div className="filter-group">
              <label htmlFor="regionFilter">Region</label>
              <select
                id="regionFilter"
                value={selectedRegion}
                onChange={handleRegionChange}
              >
                <option value="">All Regions</option>
                <option value="North America">North America</option>
                <option value="Latin America">Latin America</option>
                <option value="Western Europe">Western Europe</option>
                <option value="Eastern Europe">Eastern Europe</option>
                <option value="Middle East">Middle East</option>
                <option value="Asia">Asia</option>
                <option value="Africa">Africa</option>
                <option value="Oceania">Oceania</option>
              </select>
            </div>

            {/* Search */}
            <div className="filter-group">
              <label>Search by Title</label>
              <input
                type="text"
                placeholder="Search by Title"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>

            {/* Group Responsible */}
            <div className="filter-group">
              <label>Group Responsible</label>
              <input
                type="text"
                placeholder="Search by group"
                value={groupResponsible}
                onChange={handleGroupResponsibleChange}
              />
            </div>

            {/* Targeted Entity */}
            <div className="filter-group">
              <label>Targeted Entity</label>
              <input
                type="text"
                placeholder="Search by targeted entity"
                value={targetedEntity}
                onChange={handleTargetedEntityChange}
              />
            </div>

            {/* Type of Report */}
            <div className="filter-group">
              <label htmlFor="dropdownFilter">Type of Report</label>
              <select
                id="dropdownFilter"
                value={selectedDropdownFilter}
                onChange={applyDropdownFilter}
              >
                <option value="">All Types</option>
                <option value="protest after-action">Protest After-Action</option>
                <option value="direct action">Direct Action</option>
                <option value="general intel">General Intel</option>
                <option value="infiltration">Infiltration</option>
              </select>
            </div>

            {/* Industry */}
            <div className="filter-group">
              <label htmlFor="industryFilter">Targeted Industry</label>
              <select
                id="industryFilter"
                value={selectedIndustryFilter}
                onChange={applyIndustryFilter}
              >
                <option value="">All Industries</option>
                <option value="Fur and Leather">Fur and Leather</option>
                <option value="Pet Industry">Pet Industry</option>
                <option value="Hunting">Hunting</option>
                <option value="Academic">Academic</option>
                <option value="Government">Government</option>
                <option value="Nuclear">Nuclear</option>
                <option value="Research – Academic">Research - Academic</option>
                <option value="Research – Breeder">Research - Breeder</option>
                <option value="Research – CRO">Research - CRO</option>
                <option value="Research – Cosmetics">Research - Cosmetics</option>
                <option value="Research – General">Research - General</option>
                <option value="Research – Government">Research - Government</option>
                <option value="Research – Pharmaceuticals">Research - Pharmaceuticals</option>
                <option value="Research – Tertiary Target">Research - Tertiary Target</option>
                <option value="Research – Transportation">Research - Transportation</option>
                <option value="Research – Other">Research - Other</option>
                <option value="Agriculture – Farms">Agriculture - Farms</option>
                <option value="Agriculture – Food Service">Agriculture - Food Service</option>
                <option value="Agriculture – General">Agriculture - General</option>
                <option value="Agriculture – Government">Agriculture - Government</option>
                <option value="Agriculture – Processing">Agriculture - Processing</option>
                <option value="Agriculture – Transportation">Agriculture - Transportation</option>
                <option value="Entertainment – Government">Entertainment - Government</option>
                <option value="Entertainment – Sports">Entertainment - Sports</option>
                <option value="Entertainment – Zoo/Park">Entertainment - Zoo/Park</option>
                <option value="Coal">Coal</option>
                <option value="Fossil Fuels">Fossil Fuels</option>
                <option value="Renewables">Renewables</option>
                <option value="Logging">Logging</option>
                <option value="Academic">Academic</option>
                <option value="Financing">Financing</option>
                <option value="Anti-Vaccine">Anti-Vaccine</option>
                <option value="GMO">GMO</option>
                <option value="Access to Medicine">Access to Medicine</option>
                <option value="Other">Other</option>
                {/* paste your full industry options list here */}
              </select>
            </div>

            {/* Date range */}
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
              />
            </div>

            {/* Location */}
            <div className="filter-group">
              <label>City</label>
              <input
                type="text"
                placeholder="City"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>State / Province</label>
              <input
                type="text"
                placeholder="State/Province"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Country</label>
              <input
                type="text"
                placeholder="Country"
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
              />
            </div>

            <button onClick={handleClear} className="clear-button full-width">
              Clear Filters
            </button>
          </aside>

          {/* RIGHT COLUMN: TITLE + (CALENDAR + GRID) */}
          <div className="calendar-main">
            {/* ✅ Title is in the MAIN column (not above sidebar) */}
            <h1 className="calendar-title">Planned Protests</h1>

            <div className="calendar-content">
              {/* LEFT: Calendar */}
              <div className="calendar-wrapper">
                <Calendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  allDayAccessor={() => true}
                  views={{ month: true, day: true }}
                  defaultView="month"
                  defaultDate={new Date()}
                  popup
                  showMultiDayTimes={false}
                  components={{ event: EventComponent }}
                  onSelectSlot={(slotInfo) => {
                    console.log('Day clicked:', slotInfo.start);
                  }}
                  eventPropGetter={(event) => {
                    const isMultiDay = !moment(event.start).isSame(
                      event.end,
                      'day'
                    );
                    return {
                      style: {
                        backgroundColor: isMultiDay ? '#9c6c31' : '#3174ad',
                        color: 'white',
                        borderRadius: '5px',
                        border: 'none',
                        padding: '2px 4px',
                      },
                    };
                  }}
                  style={{ height: '100vh' }}
                />
              </div>

              {/* RIGHT: Reports grid */}
              <div className="calendar-grid-wrapper">
                <div className="reports-grid">
                  <div className="reports-header">
                    <div className="header-item">Title</div>
                    <div className="header-item">Incident Date</div>
                    <div className="header-item">Location</div>
                    <div className="header-item">Sector</div>
                  </div>

                  {filteredReports.map((report) => {
                    const fields = report.fields || {};
                    const location = [
                      fields.field_5, // City
                      fields.field_4, // State/Province
                      fields.field_3, // Country
                    ]
                      .filter(Boolean)
                      .join(', ');

                    const handleRowClick = (e) => {
                      e.preventDefault();
                      if (!fields.Links1) return;

                      const userEmail = localStorage.getItem('user-email');
                      const lastPart = (fields.Links1 || '').split('/').pop() || '';
                      const fileName = decodeURIComponent(lastPart.split('?')[0]);
                      const proxyUrl = `/api/reports/${encodeURIComponent(fileName)}`;

                      fetch('/api/log-report', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        keepalive: true,
                        body: JSON.stringify({
                          reportName: fileName,
                          reportType: 'CalendarGrid',
                          userEmail,
                        }),
                      }).catch(() => {});

                      window.open(proxyUrl, '_blank', 'noopener,noreferrer');
                    };

                    return (
                      <div className="report-row" key={fields.id || fields.ID}>
                        {fields.Links1 ? (
                          <a
                            href={fields.Links1}
                            className="report-item report-title"
                            data-label="Title"
                            onClick={handleRowClick}
                          >
                            {fields.Title}
                          </a>
                        ) : (
                          <div className="report-item" data-label="Title">
                            {fields.Title}
                          </div>
                        )}

                        <div className="report-item" data-label="Incident Date">
                          {fields.field_2_formatted}
                        </div>

                        <div className="report-item" data-label="Location">
                          {location}
                        </div>

                        <div className="report-item" data-label="Sector">
                          {Array.isArray(fields.field_6)
                            ? fields.field_6.join(', ')
                            : fields.field_6 || ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MOBILE: keep stacked layout (no grid) */}
      {isMobile && (
        <>
          <h1 className="calendar-title">Planned Protests</h1>

          <div className="mobile-filters">
            {/* you can copy the same filter inputs as on Reports mobile if you want parity */}
            <button onClick={handleClear} className="clear-button">
              Clear
            </button>
          </div>

          <div className="calendar-wrapper">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              allDayAccessor={() => true}
              views={{ month: true, day: true }}
              defaultView="month"
              defaultDate={new Date()}
              popup
              showMultiDayTimes={false}
              components={{ event: EventComponent }}
              onSelectSlot={(slotInfo) => {
                console.log('Day clicked:', slotInfo.start);
              }}
              eventPropGetter={(event) => {
                const isMultiDay = !moment(event.start).isSame(event.end, 'day');
                return {
                  style: {
                    backgroundColor: isMultiDay ? '#9c6c31' : '#3174ad',
                    color: 'white',
                    borderRadius: '5px',
                    border: 'none',
                    padding: '2px 4px',
                  },
                };
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default YourCalendarComponent;
